import { UIColor, $color } from '../ui/color';
import { $path } from '../ui/path';
import { UISVGContent, $svg, UISVG } from '../ui/svg';
import { UIRect, UIPoint, UIRelPos } from '../ui/types';
import { CHXYAxes, CHXYPlot } from './axes';
import { CHPoint } from './point';
import { CHScale } from './scale';

interface CHColumnPlotOptions {
    color?: UIColor;
}

export class CHColumnPlot<YValue> implements UISVGContent, CHXYPlot<number, YValue> {
    root: SVGGElement;
    axes: CHXYAxes<number, YValue> | undefined;

    private _data: CHPoint<string, YValue>[];
    private _rect?: UIRect;
    private _x_scale!: CHScale<number>;
    private _y_scale!: CHScale<YValue>;
    private _options: CHColumnPlotOptions;
    private _enabled: boolean;

    constructor(data: CHPoint<string, YValue>[], options: CHColumnPlotOptions = {}) {
        this.root = $svg.G('ch-bars');

        this._enabled = true;
        this._data = data;
        this._options = options;
    }

    minMax(): [[number, number], [YValue, YValue]] {
        const data = this._data;

        let min_y: YValue = Infinity as any;
        let max_y: YValue = -Infinity as any;

        for(const p of data) {
            let y = p.y;
            if(y < min_y) min_y = y;
            if(y > max_y) max_y = y;
        }

        return [[0, data.length], [min_y, max_y]];
    }

    layout(rect: UIRect, x_scale: CHScale<number>, y_scale: CHScale<YValue>) {
        const root = this.root;
        root.innerHTML = '';
        const data = this._data;
        this._rect = rect;
        this._x_scale = x_scale;
        this._y_scale = y_scale;

        if(data.length < 1) return;

        this.root.setAttribute('transform', `translate(${rect[0]},${rect[1]})`);

        const w = rect[2] - rect[0];
        const h = rect[3] - rect[1];

        const y0 = (1 - y_scale.normalize(0 as any)) * h;

        const points: number[] = [];

        for(let i = 0; i < data.length; i++) {
            $path.start();
            const x_norm = x_scale.normalize(i);
            const y_norm = y_scale.normalize(data[i].y);
            const x = x_norm * w;
            const y = (1 - y_norm) * h;
            points.push(x);
            $path.bar(x - 30, y0, x + 30, y, 10, Math.PI * 2 / 5);

            const elem = $path.toElem();
            if(this._options.color)
                elem.style.fill = $color.HTML(this._options.color);
            root.appendChild(elem);
        }
    }

    hitTest(point: UIPoint | undefined) {
        if(point) {
            const w = this._rect![2] - this._rect![0];
            const h = this._rect![3] - this._rect![1];
            let x = this._x_scale.denormalize(point[0] / w);

            x = Math.floor(x);

            const data = this._data;

            if(x < 0 || x >= data.length) return;

            const x_norm = this._x_scale.normalize(x);
            const y_norm = this._y_scale.normalize(data[x].y);

            const p_x = x_norm * w;
            const p_y = (1 - y_norm) * h;

            return {
                distance: 0,
                text: data[x].x + ' - ' + this._y_scale.tipFormat(data[x].y),
                pos: [p_x + this._rect![0], p_y + this._rect![1] - 12] as UIPoint,
                rel_pos: UIRelPos.Above
            }
        } else {
            return;
        }
    }

    render(svg: UISVG) {
        svg.add(this.root);
    }

    get enabled() {
        return this._enabled;
    }

    set enabled(e: boolean) {
        this._enabled = e;
        this.axes && this.axes.onPlotEnabled(this);
    }
}
